<template>
  <div class="media-info-com" v-show="mediaLoaded">
    <!-- 左侧媒体内容区域 -->
    <div class="m-i-c-left">
      <!-- 标题区域：告警名称和创建时间 -->
      <div class="media-name">
        <div class="m-n-info1">{{ currentMedia?.algorithm_name || currentMedia?.alarm_message }}</div>
        <div class="m-n-info2">
          <clock-circle-outlined />
          {{ currentMedia?.alarm_time }}
        </div>
      </div>

      <!-- 左切换按钮（全局文件切换） -->
      <div class="media-left-btn" @click="prevFile">
        <left-outlined />
      </div>

      <!-- 右切换按钮（全局文件切换） -->
      <div :class="['media-right-btn', { 'media-right-btn-full': isFullscreen }]" @click="nextFile">
        <right-outlined />
      </div>

      <!-- 加载遮罩 -->
      <div class="load-mask-bg" v-show="viewLoading"></div>
      <div v-show="viewLoading" class="load-mask">
        <div class="css-1dowtov ant-spin ant-spin-lg ant-spin-spinning ant-spin-show-text loading">
          <span class="ant-spin-dot ant-spin-dot-spin">
            <i class="ant-spin-dot-item"></i>
            <i class="ant-spin-dot-item"></i>
            <i class="ant-spin-dot-item"></i>
            <i class="ant-spin-dot-item"></i>
          </span>
          <div class="ant-spin-text">图片加载中...</div>
        </div>
      </div>

      <!-- 媒体内容 -->
      <div v-if="mediaLoaded" class="media-content">
        <!-- 普通图片查看器（多图，当前告警内的所有图片） -->
        <div v-show="currentFileType === 'IMAGE'"
          :class="['imgViewer-com', { 'imgViewer-com-full': isFullscreen }]">
          <div id="imgViewer" v-if="currentMedia">
            <ul>
              <!-- 遍历当前告警的所有图片文件，生成多个 img 供 Viewer.js 使用 -->
              <li v-for="file in imageFiles" :key="file.id">
                <img
                  :src="file.url"
                  :alt="file.fileName"
                  :data-original="file.url"
                  :style="{ 'display': 'none' }"
                />
              </li>
            </ul>
          </div>
        </div>

        <!-- 视频查看器（使用当前文件的 URL） -->
        <div v-if="currentFileType === 'VIDEO'" :class="['videoViewer-com', { 'videoViewer-com-full': isFullscreen }]">
          <video id="videoViewer" class="video-player" ref="videoPlayer" :controls="false" preload="metadata"
            @play="onVideoPlay" @pause="onVideoPause" @ended="onVideoEnded" @timeupdate="onVideoTimeUpdate"
            @loadedmetadata="onVideoLoadedMetadata">
            <source :src="currentFile?.url" :type="getVideoMimeType(currentFile)" />
            您的浏览器不支持视频播放。
          </video>
        </div>

        <!-- 操作区域 -->
        <div :class="['media-operate', { 'media-operate-full': isFullscreen }]">
          <!-- 文件序号显示 -->
          <div class="total-page-box">
            <!-- <div class="total-page-num">第 {{ currentFlatIndex + 1 }} / {{ totalFiles }} 个文件</div> -->
            <div class="total-page-num">第 {{ page.currentPage }} 页</div>
            <div class="total-page-size">共 {{ page.totalPage }} 页</div>
          </div>

          <!-- 图片操作按钮（仅图片类型且非全屏时显示） -->
          <div v-if="currentFileType === 'IMAGE'" class="m-o-btn-list">
            <div class="btn-li" @click="viewerZoomIn">
              <i class="iconfont icon-zoom-in"></i>
            </div>
            <div class="btn-li" @click="viewerZoomOut">
              <i class="iconfont icon-zoom-out"></i>
            </div>
            <div class="btn-li" @click="viewerResetZoom">1:1</div>
            <div class="btn-li" @click="viewerRotateLeft">
              <i class="iconfont icon-rotate-left"></i>
            </div>
            <div class="btn-li" @click="toggleFullscreen">
              <i class="iconfont icon-full-screen"></i>
            </div>
            <!-- <div class="btn-li" @click="toggleMapDisplay">
              <i :class="['iconfont', showMapStatus ? 'icon-map-off' : 'icon-map-on']"></i>
            </div> -->
          </div>

          <!-- 视频操作按钮 -->
          <div v-if="currentFileType === 'VIDEO'" class="m-o-btn-list">
            <div class="btn-li" @click="toggleVideoPlay">
              <i :class="['iconfont', isVideoPlaying ? 'icon-icon-pause' : 'icon-play']"></i>
            </div>
            <div class="btn-li" @click="toggleFullscreen">
              <i class="iconfont icon-full-screen"></i>
            </div>
          </div>

          <!-- 视频进度条 -->
          <div v-if="currentFileType === 'VIDEO'" class="video-progress-container">
            <div class="video-progress-bar" ref="progressBar" @click="onProgressBarClick">
              <div class="video-progress" :style="{ width: videoProgress + '%' }"></div>
              <div class="video-progress-handle" :style="{ left: videoProgress + '%' }"></div>
            </div>
            <div class="video-time">
              <span>{{ formatVideoTime(currentVideoTime) }}</span> /
              <span>{{ formatVideoTime(videoDuration) }}</span>
            </div>
          </div>

          <!-- 缩略图列表（当前页所有文件） -->
          <div v-if="!isFullscreen" class="m-o-img-list">
            <!-- 左切换按钮（切换告警分页） -->
            <div v-show="page.currentPage > 1" class="media-left-btn" @click="prevPage">
              <left-outlined />
            </div>

            <div class="img-list-content">
              <div
                v-for="(item, flatIndex) in flatFileList"
                :key="item.file.id"
                :class="['img-preview', {
                  'active': currentFlatIndex === flatIndex,
                  'video-preview': item.file.file_type === 'VIDEO'
                }]" @click="switchFile(flatIndex)"
              >
                <img :src="item.file.abbreviation_url || item.file.url" :alt="item.file.fileName" loading="lazy" width="84" height="72" />
                <div class="img-tips" v-if="item.file.origin === false">
                  <SvgIcon name="AIshibie" :style="{color: '#ffffff', fontSize: '20px'}"/>
                </div>
                <div v-if="item.file.file_type === 'VIDEO'" class="video-badge">
                  <i class="iconfont icon-play"></i>
                </div>
              </div>
            </div>

            <!-- 右切换按钮（切换告警分页） -->
            <div v-show="page.currentPage !== page.totalPage" :class="['media-right-btn', { 'media-right-btn-full': isFullscreen }]" @click="nextPage">
              <right-outlined />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 右侧详情信息区域（使用当前文件的数据） -->
    <div class="m-i-c-right" v-if="!isFullscreen">
      <!-- 关闭按钮 -->
      <div class="m-i-c-close" @click="closeMediaInfo">
        <close-outlined class="icon_close" />
      </div>

      <!-- 详细信息 -->
      <div class="detail-info">
        <div class="d-i-title">详细信息</div>

        <!-- 视频信息 -->
        <div v-if="currentFileType === 'VIDEO'" class="video-info">
          <div class="d-i-dataList">
            <div class="dataList-key">视频名称</div>
            <!-- 编辑名称（告警名称）
            <div v-if="isEditingName" class="d-i-dataList">
              <div class="dataList-val d-a-name">
                <input v-model="editName" type="text" class="name-input" @keyup.enter="saveEditName"
                  @blur="cancelEditName" ref="nameInputRef" :maxlength="32" />
                <span class="name-edit-btn">
                  <check-outlined class="icon_check" @click="saveEditName" />
                  <close-outlined class="icon_close" @click="cancelEditName" />
                </span>
              </div>
            </div> -->
            <div class="dataList-val d-a-name"> <!--v-else-->
              <span class="name">{{ currentMedia?.alarm_message }}</span>
              <!-- <span class="name-edit-btn" @click="startEditName">
                <edit-outlined class="icon_edit" />
              </span> -->
            </div>
          </div>

          <div class="d-i-dataList">
            <div class="dataList-key">算法模型</div>
            <div class="dataList-val">{{ currentMedia?.algorithm_name || '--' }}</div>
          </div>
          <div class="d-i-dataList">
            <div class="dataList-key">告警类型</div>
            <div class="dataList-val">{{ currentFile?.origin ? '普通视频'  : 'AI分析视频' }}</div>
          </div>
          <div class="d-i-dataList">
            <div class="dataList-key">经度</div>
            <div class="dataList-val">{{ currentFile?.lon ? Number(currentFile.lon).toFixed(6) : '--' }}</div>
          </div>
          <div class="d-i-dataList">
            <div class="dataList-key">纬度</div>
            <div class="dataList-val">{{ currentFile?.lat ? Number(currentFile.lat).toFixed(6) : '--' }}</div>
          </div>
          <div class="d-i-dataList">
            <div class="dataList-key">拍摄负载</div>
            <div class="dataList-val">{{ currentMedia?.device_name || '--' }}</div>
          </div>
          <div class="d-i-dataList">
            <div class="dataList-key">告警时间</div>
            <div class="dataList-val">{{ currentMedia?.alarm_time || '--' }}</div>
          </div>
        </div>

        <!-- 图片信息 -->
        <div v-else class="img-info">
          <div class="d-i-dataList">
            <div class="dataList-key">告警信息</div>
            <!-- 编辑名称（告警名称）
            <div v-if="isEditingName" class="d-i-dataList">
              <div class="dataList-val d-a-name">
                <input v-model="editName" type="text" class="name-input" @keyup.enter="saveEditName"
                  @blur="cancelEditName" ref="nameInputRef" :maxlength="32" />
                <span class="name-edit-btn">
                  <check-outlined class="icon_check" @click="saveEditName" />
                  <close-outlined class="icon_close" @click="cancelEditName" />
                </span>
              </div>
            </div>-->
            <div class="dataList-val d-a-name"><!-- v-else-->
              <span class="name">{{ currentMedia?.alarm_message }}</span>
              <span class="name-edit-btn" @click="downloadMedia">
                <DownloadOutlined class="icon_edit fz20" />
              </span>
              <!-- <span class="name-edit-btn" @click="startEditName">
                <edit-outlined class="icon_edit" />
              </span> -->
            </div>
          </div>

          <div class="d-i-dataList">
            <div class="dataList-key">算法模型</div>
            <div class="dataList-val">{{ currentMedia?.algorithm_name || '--' }}</div>
          </div>
          <div class="d-i-dataList">
            <div class="dataList-key">告警类型</div>
            <div class="dataList-val">{{ currentFile?.origin ? '普通图'  : 'AI分析图' }}</div>
          </div>
          <div class="d-i-dataList">
            <div class="dataList-key">经度</div>
            <div class="dataList-val">{{ currentFile?.lon ? Number(currentFile.lon).toFixed(6) : '--' }}</div>
          </div>
          <div class="d-i-dataList">
            <div class="dataList-key">纬度</div>
            <div class="dataList-val">{{ currentFile?.lat ? Number(currentFile.lat).toFixed(6) : '--' }}</div>
          </div>
          <div class="d-i-dataList">
            <div class="dataList-key">拍摄负载</div>
            <div class="dataList-val">{{ currentMedia?.device_name || '--' }}</div>
          </div>
          <div class="d-i-dataList">
            <div class="dataList-key">告警时间</div>
            <div class="dataList-val">{{ currentMedia?.alarm_time || '--' }}</div>
          </div>
        </div>

        <div>
          <div class="d-i-dataList">
            <div class="dataList-key">文件位置</div>
            <div class="d-i-dataList">
              <div class="dataList-map" ref="mapContainer"></div>
            </div>
          </div>
        </div>

        <!-- 操作按钮
        <div class="detail-btn">
          收藏按钮
          <div class="d-b-li" @click="toggleFavorite">
            <i :class="['iconfont', currentMedia?.favorites ? 'icon-shoucang' : 'icon-weishoucang']"></i>
            <div class="btn-txt">{{ currentMedia?.favorites ? '取消收藏' : '收藏' }}</div>
          </div>

          下载按钮
          <div class="d-b-li" @click="downloadMedia">
            <i class="iconfont icon-download"></i>
            <div class="btn-txt">下载</div>
          </div>
        </div> -->
      </div>
    </div>

    <!-- 分屏对比模态框（保留） -->
    <media-contrast v-if="showContrast" :origin-img="originImage" :origin-img-id="originImageId"
      :origin-img-name="originImageName" @close="closeContrast" />

    <!-- 删除确认模态框 -->
    <deleteConfirm v-model:open="showDeleteModal" :mediaName="currentMedia?.name" @confirm="confirmDelete"
      @cancel="closeDeleteModal"></deleteConfirm>
  </div>
</template>

<script setup lang="ts">
import { message } from 'ant-design-vue'
import { ref, computed, onMounted, onUnmounted, nextTick, defineProps, defineEmits, defineExpose, reactive, watch } from 'vue'
import ImageViewer from 'viewerjs'
import 'viewerjs/dist/viewer.css'
import MediaContrast from './media-contrast.vue'
import deleteConfirm from './delete-confirm.vue'
import { getAlarmPage } from '/@/api/alarm'
import { ClockCircleOutlined, LeftOutlined, RightOutlined, CloseOutlined } from '@ant-design/icons-vue'
import * as Cesium from 'cesium'
import { AmapImageryProvider } from '/@/hooks/Cesium/image-provider'

const props = defineProps({
  tagOptions: { type: Array, default: () => [] },
  jobId: { type: String, default: '' }
})

const emit = defineEmits(['closeMediaInfo', 'updateMediaFileList', 'toggleDisplay'])

// ---------- 响应式数据 ----------
const mediaList = ref<any[]>([]) // 告警列表
const flatFileList = ref<{ file: any; mediaIndex: number; fileIndex: number }[]>([]) // 扁平文件列表
const currentFlatIndex = ref(0) // 当前文件在扁平列表中的索引
const mediaLoaded = ref(false) // 组件是否已加载
const isFullscreen = ref(false) // 是否全屏模式
const viewLoading = ref(false) // 图片加载中

// 视频相关
const videoPlayer = ref<HTMLVideoElement | null>(null)
const isVideoPlaying = ref(false)
const videoDuration = ref(0)
const currentVideoTime = ref(0)
const videoProgress = ref(0)
const progressBar = ref<HTMLDivElement | null>(null)

// 编辑名称相关
const isEditingName = ref(false)
const editName = ref('')
const nameInputRef = ref<HTMLInputElement | null>(null)

// 地图显示状态
const showMapStatus = ref(false)

// 分屏对比相关
const showContrast = ref(false)
const originImage = ref('')
const originImageId = ref('')
const originImageName = ref('')

// 删除相关
const showDeleteModal = ref(false)

// 分页参数
const page = reactive({
  currentPage: 1,
  pageSize: 2, // 示例值，实际可能由外部传入
  totalPage: 1,
  totalCount: 0
})

// Viewer.js 实例
let viewerInstance: ImageViewer | null = null

// ---------- 计算属性 ----------
// 当前文件对象
const currentFile = computed(() => {
  return flatFileList.value[currentFlatIndex.value]?.file || null
})

// 当前文件类型
const currentFileType = computed(() => currentFile.value?.file_type || 'IMAGE')

// 当前告警对象（通过当前文件所属告警索引获取）
const currentMedia = computed(() => {
  const item = flatFileList.value[currentFlatIndex.value]
  return item ? mediaList.value[item.mediaIndex] : null
})

// 当前告警下的所有图片文件（用于图片查看器）
const imageFiles = computed(() => {
  return currentMedia.value?.files?.filter((f: any) => f.file_type === 'IMAGE') || []
})

// 当前页文件总数
const totalFiles = computed(() => flatFileList.value.length)

// ---------- 辅助函数：构建扁平文件列表 ----------
const buildFlatFileList = () => {
  const list: { file: any; mediaIndex: number; fileIndex: number }[] = []
  mediaList.value.forEach((media, mediaIndex) => {
    if (media.files && Array.isArray(media.files)) {
      media.files.forEach((file: any, fileIndex: number) => {
        list.push({ file, mediaIndex, fileIndex })
      })
    }
  })
  flatFileList.value = list
}

// ---------- 方法 ----------
/**
 * 初始化数据（供父组件调用）
 */
const initData = async () => {
  page.currentPage = 1
  mediaList.value = []
  flatFileList.value = []
  currentFlatIndex.value = 0

  const queryParam = {
    page: page.currentPage,
    page_size: page.pageSize,
    param: { job_id: props.jobId }
  }
  const res = await getAlarmPage(queryParam)
  if (res && res.code === 0) {
    mediaList.value = res.data.list
    page.totalCount = res.data.pagination.total
    page.totalPage = res.data.pagination.total ? Math.ceil(page.totalCount / page.pageSize) : 1
    buildFlatFileList()
  } else {
    mediaList.value = []
    page.totalPage = 1
  }

  mediaLoaded.value = true

  nextTick(() => {
    if (currentFileType.value === 'IMAGE') {
      initImageViewer()
    } else if (currentFileType.value === 'VIDEO') {
      initVideoPlayer()
    }
  })
}

/**
 * 获取当前页告警（分页切换时调用）
 */
const getPageMedia = async () => {
  destroyImageViewer()

  const queryParam = {
    page: page.currentPage,
    page_size: page.pageSize,
    param: { job_id: props.jobId }
  }
  const res = await getAlarmPage(queryParam)
  if (res && res.code === 0) {
    mediaList.value = res.data.list
    page.totalCount = res.data.pagination.total
    page.totalPage = res.data.pagination.total ? Math.ceil(page.totalCount / page.pageSize) : 1
    buildFlatFileList()
    currentFlatIndex.value = 0
  } else {
    mediaList.value = []
    page.totalPage = 1
  }

  nextTick(() => {
    if (currentFileType.value === 'IMAGE') {
      initImageViewer()
    } else if (currentFileType.value === 'VIDEO') {
      initVideoPlayer()
    }
  })
}

/**
 * 切换文件（通过扁平索引）
 */
const switchFile = (flatIndex: number) => {
  if (flatIndex < 0 || flatIndex >= flatFileList.value.length) return
  currentFlatIndex.value = flatIndex

  destroyImageViewer()

  nextTick(() => {
    if (currentFileType.value === 'IMAGE') {
      initImageViewer()
    } else if (currentFileType.value === 'VIDEO') {
      initVideoPlayer()
    }
  })
}

const prevFile = () => {
  if (flatFileList.value.length === 0) return
  let newIndex = currentFlatIndex.value - 1
  if (newIndex < 0) newIndex = flatFileList.value.length - 1
  switchFile(newIndex)
}

const nextFile = () => {
  if (flatFileList.value.length === 0) return
  let newIndex = currentFlatIndex.value + 1
  if (newIndex >= flatFileList.value.length) newIndex = 0
  switchFile(newIndex)
}

const prevPage = () => {
  if (page.currentPage === 1) {
    message.info('已经是第一页了')
  } else {
    page.currentPage--
    getPageMedia()
  }
}

const nextPage = () => {
  if (page.currentPage === page.totalPage) {
    message.info('已经是最后一页了')
  } else {
    page.currentPage++
    getPageMedia()
  }
}

// ---------- 图片查看器 (Viewer.js) ----------
const initImageViewer = () => {
  destroyImageViewer()

  nextTick(() => {
    const container = document.getElementById('imgViewer')
    if (!container) {
      console.error('找不到 imgViewer 容器')
      return
    }

    const images = Array.from(container.querySelectorAll('img'))
    if (!images.length) {
      console.error('没有找到图片元素')
      return
    }

    // 确定当前文件在当前告警图片列表中的索引
    const targetIndex = imageFiles.value.findIndex(f => f.id === currentFile.value?.id)
    const initialIndex = targetIndex >= 0 ? targetIndex : 0

    try {
      viewLoading.value = true
      viewerInstance = new ImageViewer(container, {
        inline: true,
        navbar: false,
        title: false,
        toolbar: false,
        rotatable: true,
        scalable: true,
        zoomable: true,
        movable: true,
        keyboard: true,
        transition: false,
        zoomRatio: 0.1,
        minZoomRatio: 0.01,
        maxZoomRatio: 100,
        initialViewIndex: initialIndex,
        ready () {
          console.log('Viewer.js 已加载')
          viewerInstance?.view(initialIndex)
        },
        viewed () {
          viewLoading.value = false
        }
      })
    } catch (error) {
      console.error('初始化图片查看器失败:', error)
      viewLoading.value = false
    }
  })
}

const destroyImageViewer = () => {
  if (viewerInstance) {
    viewerInstance.destroy()
    viewerInstance = null
  }
}

// 图片操作
const viewerZoomIn = () => viewerInstance?.zoom(0.1)
const viewerZoomOut = () => viewerInstance?.zoom(-0.1)
const viewerResetZoom = () => viewerInstance?.reset()
const viewerRotateLeft = () => viewerInstance?.rotate(-90)

// ---------- 视频播放器 ----------
const initVideoPlayer = () => {
  const video = videoPlayer.value
  if (!video) return

  isVideoPlaying.value = false
  videoDuration.value = 0
  currentVideoTime.value = 0
  videoProgress.value = 0

  if (currentFile.value?.url) {
    video.src = currentFile.value.url
  }
}

const onVideoPlay = () => { isVideoPlaying.value = true }
const onVideoPause = () => { isVideoPlaying.value = false }
const onVideoEnded = () => {
  isVideoPlaying.value = false
  currentVideoTime.value = videoDuration.value
  videoProgress.value = 100
}
const onVideoTimeUpdate = () => {
  const video = videoPlayer.value
  if (video) {
    currentVideoTime.value = video.currentTime
    videoDuration.value = video.duration || 0
    if (videoDuration.value > 0) {
      videoProgress.value = (currentVideoTime.value / videoDuration.value) * 100
    }
  }
}
const onVideoLoadedMetadata = () => {
  const video = videoPlayer.value
  if (video) {
    videoDuration.value = video.duration || 0
  }
}
const toggleVideoPlay = () => {
  const video = videoPlayer.value
  if (!video) return
  if (video.paused) {
    video.play().catch(console.error)
  } else {
    video.pause()
  }
}
const onProgressBarClick = (event: MouseEvent) => {
  const video = videoPlayer.value
  if (!video || !progressBar.value || videoDuration.value <= 0) return
  const rect = progressBar.value.getBoundingClientRect()
  const clickPosition = event.clientX - rect.left
  const percentage = (clickPosition / rect.width) * 100
  const newTime = (percentage / 100) * videoDuration.value
  video.currentTime = newTime
  currentVideoTime.value = newTime
  videoProgress.value = percentage
}

const getVideoMimeType = (file: any) => {
  if (!file || !file.file_type) return 'video/mp4'
  const typeMap: Record<string, string> = {
    mp4: 'video/mp4',
    webm: 'video/webm',
    ogg: 'video/ogg',
    mov: 'video/quicktime',
    avi: 'video/x-msvideo',
    wmv: 'video/x-ms-wmv'
  }
  return typeMap[file.file_type.toLowerCase()] || 'video/mp4'
}

// ---------- 通用操作 ----------
const toggleFullscreen = () => {
  isFullscreen.value = !isFullscreen.value
}

const toggleMapDisplay = () => {
  emit('toggleDisplay', currentMedia.value, !showMapStatus.value, 'map_load', () => {
    showMapStatus.value = !showMapStatus.value
  })
}

const closeMediaInfo = () => {
  mediaLoaded.value = false
  destroyImageViewer()
  emit('closeMediaInfo')
}

// 编辑名称（操作的是告警名称）
const startEditName = () => {
  isEditingName.value = true
  editName.value = currentMedia.value?.alarm_message || ''
  nextTick(() => nameInputRef.value?.focus())
}
const saveEditName = async () => {
  if (!editName.value.trim()) return
  emit('toggleDisplay', currentMedia.value, editName.value, 'alarm_message', () => {
    isEditingName.value = false
    if (currentMedia.value) currentMedia.value.alarm_message = editName.value
  })
}
const cancelEditName = () => {
  isEditingName.value = false
  editName.value = ''
}

// 收藏
const toggleFavorite = async () => {
  emit('toggleDisplay', currentMedia.value, !currentMedia.value?.favorites, 'favorites', () => {
    if (currentMedia.value) currentMedia.value.favorites = !currentMedia.value.favorites
  })
}

// 下载当前文件
const downloadMedia = async () => {
  const file = currentFile.value
  console.log('currentFile.value', currentFile.value)
  if (!file?.url) {
    message.warning('文件URL不存在，无法下载')
    return
  }

  const fileName = file.file_url || `download.${file.file_type || 'bin'}`

  try {
    // 方案1：通过 fetch 获取二进制流并生成 Blob 下载
    const response = await fetch(file.url, {
      method: 'GET',
      headers: { Accept: '*/*' }
    })

    if (!response.ok) {
      throw new Error(`HTTP错误 ${response.status}`)
    }

    const blob = await response.blob()
    const blobUrl = window.URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = blobUrl
    link.download = fileName
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()

    setTimeout(() => {
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
    }, 100)

    // message.success({ content: `开始下载：${fileName}`, key: 'download' })
  } catch (error) {
    console.error('下载失败：', error)

    // 方案2：降级处理，在新标签页打开（让用户手动保存）
    const link = document.createElement('a')
    link.href = file.url
    link.download = fileName
    link.target = '_blank'
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    message.warning({
      content: '自动下载失败，请在新打开的页面中右键选择「另存为」',
      key: 'download',
      duration: 5
    })
  }
}

// 删除相关
const openDeleteModal = () => { showDeleteModal.value = true }
const closeDeleteModal = () => { showDeleteModal.value = false }
const confirmDelete = async () => {
  console.log('删除媒体文件')
  showDeleteModal.value = false
  // emit('deleteMediaFile', currentMedia.value?.id)
  closeMediaInfo()
}

// 分屏对比
const openSplitScreen = () => {
  if (currentFileType.value === 'IMAGE') {
    originImage.value = currentFile.value?.url || ''
    originImageId.value = currentFile.value?.id || ''
    originImageName.value = currentFile.value?.fileName || ''
    showContrast.value = true
  }
}
const closeContrast = () => { showContrast.value = false }

// ---------- 工具函数 ----------
const formatTime = (time: string) => time ? new Date(time).toLocaleString() : '--'
const formatDuration = (seconds: number) => {
  if (!seconds) return '--'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}
const formatVideoTime = (time: number) => {
  if (!time || time <= 0) return '00:00'
  const mins = Math.floor(time / 60)
  const secs = Math.floor(time % 60)
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

// ---------- 地图 ----------
const mapContainer = ref<HTMLElement | null>(null)
let cesiumViewer: Cesium.Viewer | null = null
let currentEntity: Cesium.Entity | null = null

const initMap = () => {
  if (!mapContainer.value || cesiumViewer) return // 防止重复初始化

  cesiumViewer = new Cesium.Viewer(mapContainer.value, {
    animation: false,
    baseLayerPicker: false,
    fullscreenButton: false,
    geocoder: false,
    homeButton: false,
    infoBox: false,
    sceneModePicker: false,
    selectionIndicator: false,
    timeline: false,
    navigationHelpButton: false,
    imageryProvider: false,
    baseLayer: false,
    targetFrameRate: 60,
    contextOptions: {
      webgl: { alpha: true, preserveDrawingBuffer: true }
    }
  })

  // 添加高德底图
  const amapLayer = new Cesium.ImageryLayer(
    new AmapImageryProvider({
      type: 'img_w',
      crs: 'WGS84'
    })
  )
  cesiumViewer.imageryLayers.add(amapLayer)

  // 默认视角
  cesiumViewer.camera.setView({
    destination: Cesium.Cartesian3.fromDegrees(113.48, 23.10, 5000)
  })
}

watch(mediaLoaded, (loaded) => {
  if (loaded && mapContainer.value && !cesiumViewer) {
    nextTick(() => {
      initMap()
    })
  }
}, { immediate: true })

watch(currentFile, (file) => {
  if (!cesiumViewer || !file) return

  // 移除之前的点标记
  if (currentEntity) {
    cesiumViewer.entities.remove(currentEntity)
    currentEntity = null
  }

  const lon = file.lon ? Number(file.lon) : null
  const lat = file.lat ? Number(file.lat) : null
  if (lon && lat && !isNaN(lon) && !isNaN(lat)) {
    // 飞行动画（保留原有逻辑）
    cesiumViewer.camera.flyTo({
      destination: Cesium.Cartesian3.fromDegrees(lon, lat, 1000),
      duration: 1.5
    })

    // 创建点标记实体
    const position = Cesium.Cartesian3.fromDegrees(lon, lat, 0) // 地面高度
    // 优先使用缩略图，如果没有则使用默认图标
    const imageUrl = file.abbreviation_url || file.url || ''
    const defaultIcon = Cesium.buildModuleUrl('Widgets/Images/blue.png') // Cesium 默认蓝色图钉

    currentEntity = cesiumViewer.entities.add({
      position: position,
      billboard: {
        image: imageUrl || defaultIcon,
        width: 90, // 可根据需要调整
        height: 65,
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM, // 图标底部对准坐标点
        scale: 1.0,
        // 如果图片加载失败，可自动降级（Cesium 默认会显示 broken image 图标，但也可以设置 fallback）
      },
      label: {
        text: file.fileName || '当前位置',
        font: '14px sans-serif',
        fillColor: Cesium.Color.WHITE,
        style: Cesium.LabelStyle.FILL_AND_OUTLINE,
        outlineWidth: 2,
        verticalOrigin: Cesium.VerticalOrigin.TOP,
        pixelOffset: new Cesium.Cartesian2(0, 10) // 在图标上方偏移显示文字
      }
    })
  }
}, { immediate: true })

// ---------- 生命周期 ----------
onMounted(() => {
  document.addEventListener('fullscreenchange', () => {
    isFullscreen.value = !!document.fullscreenElement
  })
  initMap()
})

onUnmounted(() => {
  destroyImageViewer()
  document.removeEventListener('fullscreenchange', () => {})
  if (cesiumViewer && !cesiumViewer.isDestroyed()) {
    if (currentEntity) {
      cesiumViewer.entities.remove(currentEntity)
    }
    cesiumViewer.destroy()
    cesiumViewer = null
  }
})

defineExpose({ initData })
</script>

<style lang="scss" scoped>
.media-info-com {
  display: flex;
  width: calc(100vw - 82px);
  height: calc(100% - 60px);
  background: #001529;
  position: fixed;
  z-index: 10;
  top: 60px;
  left: 82px;

  .m-i-c-left {
    flex: 1;
    display: flex;
    flex-direction: column;
    position: relative;
    border-right: 1px solid #303030;
    overflow: hidden;

    .media-name {
      padding: 16px 24px;
      border-bottom: 1px solid #303030;
      flex-shrink: 0;
      background: #1f1f1f;

      .m-n-info1 {
        font-size: 16px;
        font-weight: 600;
        color: #ffffff;
        margin-bottom: 6px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .m-n-info2 {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 13px;
        color: rgba(255, 255, 255, 0.65);
      }
    }

    .media-left-btn,
    .media-right-btn {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      width: 40px;
      height: 40px;
      background: rgba(0, 0, 0, 0.6);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      z-index: 10;
      color: #fff;
      border: 1px solid rgba(255, 255, 255, 0.15);
      transition: all 0.2s ease;

      &:hover {
        background: rgba(0, 0, 0, 0.8);
        border-color: rgba(255, 255, 255, 0.25);
        transform: translateY(-50%) scale(1.05);
      }

      i {
        font-size: 20px;
        color: #ffffff;
      }
    }

    .media-left-btn {
      left: 16px;
    }

    .media-right-btn {
      right: 16px;

      &.media-right-btn-full {
        right: 32px;
      }
    }

    .load-mask {
      position: absolute;
      left: -1%;
      top: -1%;
      z-index: 110;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 102%;
      height: calc(100% - 44px);
    }

    .load-mask-bg {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: calc(100% - 164px);
      z-index: 109;
      background-color: rgba(0, 0, 0, 0.5);
    }

    .media-content {
      flex: 1;
      position: relative;
      overflow: hidden;
      background: #000000;

      .imgViewer-com {
        width: 100%;
        height: calc(100% - 164px);
        position: relative;

        &.imgViewer-com-full {
          width: 100%;
          height: 100%;
          z-index: 1000;
        }

        #imgViewer {
          width: 100%;
          height: 100%;
          position: relative;
          overflow: hidden;

          ul {
            display: flex;
            list-style: none;
            padding: 0;
            margin: 0;
            width: 100%;
            height: 100%;
            position: absolute;
            top: 0;
            left: 0;

            li {
              flex: 0 0 100%;
              width: 100%;
              height: 100%;
              position: relative;

              img {
                width: 100%;
                height: 100%;
                object-fit: contain;
                display: none;
              }
            }
          }
        }
      }

      .videoViewer-com {
        width: 100%;
        height: 100%;
        position: relative;

        &.videoViewer-com-full {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          z-index: 1000;
        }

        .video-player {
          width: 100%;
          height: 100%;
          background: #000000;
          outline: none;
        }
      }

      .video-progress-container {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 0 16px 16px;

        .video-progress-bar {
          flex: 1;
          height: 4px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 2px;
          cursor: pointer;
          position: relative;

          &:hover {
            height: 6px;

            .video-progress-handle {
              display: block;
            }
          }

          .video-progress {
            height: 100%;
            background: #1890ff;
            border-radius: 2px;
            transition: width 0.1s ease;
          }

          .video-progress-handle {
            position: absolute;
            top: 50%;
            transform: translate(-50%, -50%);
            width: 12px;
            height: 12px;
            background: #1890ff;
            border-radius: 50%;
            display: none;
          }
        }

        .video-time {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.7);
          min-width: 80px;
          text-align: right;
        }
      }

      .total-page-box {
        position: absolute;
        width: 100%;
        z-index: 6;
        height: 35px;

        .total-page-num {
          position: absolute;
          bottom: 0;
          left: 38%;
          color: #ffffff;
          font-size: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          height: 35px;
        }
        .total-page-size{
          position: absolute;
          bottom: 0;
          right: 37.5%;
          color: #ffffff;
          font-size: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          height: 35px;
        }
      }

      .media-operate {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.8) 100%);
        padding: 16px 0;
        z-index: 5;

        &.media-operate-full {
          // position: fixed;
          bottom: 0;
          z-index: 1001;
          background: rgba(0, 0, 0, 0.9);
        }

        .m-o-btn-list {
          display: flex;
          justify-content: center;
          gap: 8px;
          margin-bottom: 16px;
          padding: 0 16px;
          position: relative;
          z-index: 10;

          .btn-li {
            width: 36px;
            height: 36px;
            border-radius: 6px;
            color: #fff;
            background: rgba(255, 255, 255, 0.1);
            border: 1px solid rgba(255, 255, 255, 0.2);
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.2s ease;

            &:hover {
              background: rgba(255, 255, 255, 0.2);
              transform: translateY(-1px);
            }

            i {
              font-size: 18px;
              color: #ffffff;
            }
          }
        }

        .m-o-img-list {
          width: 100%;
          padding: 0 16px;
          display: flex;
          align-items: center;
          justify-content: center;

          .media-left-btn,
          .media-right-btn {
            position: relative;
            top: 20px;
            left: 16px;
          }

          .media-left-btn {
            left: -16px;
          }

          .img-list-content {
            display: flex;
            gap: 8px;
            justify-content: flex-start;
            padding: 4px 0;
            width: auto;
            overflow-x: auto;
          }

          .img-preview {
            width: 84px;
            height: 72px;
            border-radius: 4px;
            overflow: hidden;
            cursor: pointer;
            position: relative;
            border: 2px solid transparent;
            flex-shrink: 0;
            transition: all 0.2s ease;

            &:hover {
              transform: translateY(-2px);
              border-color: rgba(24, 144, 255, 0.3);
            }

            &.active {
              border-color: #1890ff;
              box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3);
            }

            img {
              width: 100%;
              height: 100%;
              object-fit: cover;
            }

            .img-tips {
              position: absolute;
              bottom: 2px;
              right: 2px;
              background: rgba(0, 0, 0, 0.5);
              color: #fff;
              font-size: 12px;
              padding: 1px 3px;
              border-radius: 2px;
            }

            .video-badge {
              position: absolute;
              top: 4px;
              left: 4px;
              background: rgba(24, 144, 255, 0.8);
              border-radius: 2px;
              padding: 1px 4px;

              i {
                font-size: 12px;
                color: white;
              }
            }
          }
        }
      }
    }
  }

  .m-i-c-right {
    width: 400px;
    display: flex;
    flex-direction: column;
    background: #1f1f1f;
    position: relative;

    .m-i-c-close {
      position: absolute;
      top: 16px;
      right: 16px;
      width: 32px;
      height: 32px;
      border-radius: 6px;
      background: rgba(255, 255, 255, 0.1);
      color: #fff;
      border: 1px solid rgba(255, 255, 255, 0.2);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      z-index: 10;
      transition: all 0.2s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.2);
        transform: rotate(90deg);
      }

      i {
        font-size: 16px;
        color: #ffffff;
      }
    }

    .detail-info {
      flex: 1;
      overflow-y: auto;
      padding: 20px 24px;

      .d-i-title {
        font-size: 16px;
        font-weight: 600;
        color: #ffffff;
        margin-bottom: 20px;
        padding-bottom: 12px;
        border-bottom: 1px solid #303030;
      }

      .d-i-dataList {
        display: flex;
        margin-bottom: 16px;

        .dataList-key {
          width: 80px;
          font-size: 13px;
          color: rgba(255, 255, 255, 0.65);
          flex-shrink: 0;
          padding-top: 2px;
        }

        .dataList-val {
          flex: 1;
          font-size: 13px;
          color: #ffffff;
          word-break: break-word;
          line-height: 1.5;

          &.d-a-name {
            display: flex;
            align-items: center;
            justify-content: space-between;

            .name {
              flex: 1;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              padding-right: 8px;
            }

            .name-edit-btn {
              flex-shrink: 0;
              display: flex;
              gap: 4px;
              cursor: pointer;

              i {
                font-size: 16px;
                transition: color 0.2s ease;

                &:hover {
                  opacity: 0.8;
                }
              }

              .icon_edit {
                color: #1890ff;
              }

              .icon_check {
                color: #52c41a;
              }

              .icon_close {
                color: #ff4d4f;
              }
            }

            .name-input {
              flex: 1;
              padding: 4px 8px;
              border: 1px solid #1890ff;
              border-radius: 4px;
              background: rgba(0, 0, 0, 0.2);
              color: #ffffff;
              font-size: 13px;

              &:focus {
                outline: none;
                border-color: #40a9ff;
              }
            }
          }
        }

        .dataList-map {
          width: 275px;
          height: 150px;
          border-radius: 2px;
        }
      }

      .icon-tag-item {
        display: inline-block;
        padding: 2px 8px;
        background: rgba(24, 144, 255, 0.1);
        border: 1px solid rgba(24, 144, 255, 0.3);
        border-radius: 12px;
        font-size: 12px;
        color: #1890ff;
        margin-right: 6px;
        margin-bottom: 6px;
        transition: all 0.2s ease;

        &:hover {
          background: rgba(24, 144, 255, 0.2);
        }
      }

      .no-tag {
        color: rgba(255, 255, 255, 0.45);
        font-style: italic;
      }

      .detail-btn {
        display: flex;
        gap: 12px;
        margin-top: 24px;
        padding-top: 20px;
        border-top: 1px solid #303030;

        .d-b-li {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          padding: 12px 0;
          border-radius: 6px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          cursor: pointer;
          transition: all 0.2s ease;

          &:hover {
            background: rgba(255, 255, 255, 0.1);
            transform: translateY(-2px);
            border-color: rgba(255, 255, 255, 0.2);
          }

          &:active {
            transform: translateY(0);
          }

          i {
            font-size: 20px;
            color: #ffffff;
          }

          .btn-txt {
            font-size: 12px;
            color: rgba(255, 255, 255, 0.8);
            font-weight: 500;
          }
        }
      }
    }
  }

  /* 删除确认模态框样式（保持原有） */
  .delete-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
  }

  .delete-modal {
    width: 400px;
    max-width: 90vw;
    padding: 32px;
    background: #001529;
    border-radius: 8px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
    text-align: center;

    .modal-icon {
      width: 60px;
      height: 60px;
      margin: 0 auto 20px;
      background: rgba(255, 77, 79, 0.1);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 2px solid rgba(255, 77, 79, 0.3);

      i {
        font-size: 28px;
        color: #ff4d4f;
      }
    }

    .modal-title {
      margin: 0 0 12px 0;
      font-size: 16px;
      font-weight: 600;
      color: #ffffff;
    }

    .modal-message {
      margin: 0 0 8px 0;
      font-size: 14px;
      color: rgba(255, 255, 255, 0.8);
      line-height: 1.5;

      .media-name {
        color: #1890ff;
        font-weight: 500;
      }
    }

    .modal-warning {
      margin: 0 0 24px 0;
      font-size: 13px;
      color: #ff4d4f;
    }

    .modal-actions {
      display: flex;
      gap: 12px;

      .btn {
        flex: 1;
        height: 36px;
        border-radius: 6px;
        border: none;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;

        &-cancel {
          background: rgba(255, 255, 255, 0.05);
          color: rgba(255, 255, 255, 0.7);
          border: 1px solid rgba(255, 255, 255, 0.15);

          &:hover {
            background: rgba(255, 255, 255, 0.1);
            color: #ffffff;
            border-color: rgba(255, 255, 255, 0.25);
          }
        }

        &-delete {
          background: #ff4d4f;
          color: #ffffff;

          &:hover {
            background: #ff7875;
          }
        }
      }
    }
  }
}

/* 响应式设计保持不变 */
@media (max-width: 1200px) {
  .media-info-com .m-i-c-right {
    width: 350px;
  }
}

@media (max-width: 992px) {
  .media-info-com {
    flex-direction: column;

    .m-i-c-left {
      height: 60vh;
      border-right: none;
      border-bottom: 1px solid #303030;
    }

    .m-i-c-right {
      width: 100%;
      height: 40vh;
    }
  }
}

@media (max-width: 768px) {
  .media-info-com .m-i-c-left {
    .media-name {
      padding: 12px 16px;
    }

    .media-left-btn,
    .media-right-btn {
      width: 36px;
      height: 36px;
      i {
        font-size: 18px;
      }
    }

    .media-operate {
      .m-o-btn-list {
        padding: 0 12px;
        .btn-li {
          width: 32px;
          height: 32px;
          i {
            font-size: 16px;
          }
        }
      }

      .m-o-img-list {
        padding: 0 12px;
        .img-preview {
          width: 70px;
          height: 60px;
        }
      }
    }
  }

  .media-info-com .m-i-c-right .detail-info {
    padding: 16px;

    .d-i-dataList {
      flex-direction: column;
      gap: 4px;
      margin-bottom: 12px;

      .dataList-key {
        width: 100%;
      }
    }

    .detail-btn {
      flex-wrap: wrap;
      gap: 8px;

      .d-b-li {
        flex: 0 0 calc(50% - 4px);
        padding: 10px 0;
        i {
          font-size: 18px;
        }
      }
    }
  }
}

@media (max-width: 576px) {
  .media-info-com {
    .m-i-c-left {
      height: 50vh;
    }
    .m-i-c-right {
      height: 50vh;
    }
  }
}
</style>
